import { z } from "zod";
import {
  formatQuantityWithUnitKey,
  TWO_DECIMALS_MESSAGE,
  TWO_DECIMALS_REFINE,
} from "@/lib/formatting";

/**
 * The customer's statement of needs from the funnel (ARCH-039 D13 as amended, D16).
 *
 * Six entries; the Bedarf step asks for four needs, because the two naturschutz denominations are
 * the two ways of stating one Biotopausgleich need. A selection is a need, never a preference, and
 * nothing here is ever summed; two denominations held at once (Sales' doing, never the customer's)
 * are two demands. The statement is stored in full in `requests.axis_hints`; `Request.transcribe`
 * makes the entries it can hold without a subject (forst, and the customer's Biotopausgleich) open
 * lines, and Sales mints the rest with the subject. Nothing is inferred (D1).
 *
 * This module is the single owner of the keys, the units, the labels, the schema, normalisation,
 * the chat merge and the parent rule. Quantities must satisfy storage precision before persistence.
 */
export const STATEMENT_KEYS = [
  "naturschutz_points",
  "naturschutz_area",
  "forst",
  "artenschutz",
  "biotop",
  "unknown",
] as const;
export type StatementKey = (typeof STATEMENT_KEYS)[number];
export type NamedStatementKey = Exclude<StatementKey, "unknown">;
const NAMED_KEYS = STATEMENT_KEYS.filter((k): k is NamedStatementKey => k !== "unknown");

/**
 * Biotopausgleich is ONE need in two denominations: the customer names Ökopunkte or Hektar, and
 * the funnel asks for exactly one of them (see `funnel-statement.ts`). Storage keeps the two keys
 * apart — Sales may end up holding both, and the transcription has to know which is which.
 */
export const BIOTOPAUSGLEICH_KEYS = ["naturschutz_points", "naturschutz_area"] as const;
export type BiotopausgleichKey = (typeof BIOTOPAUSGLEICH_KEYS)[number];
export const BIOTOPAUSGLEICH_LABEL = "Biotopausgleich (Ökopunkte oder Ausgleichsfläche)";
/** How the two denominations are offered — the toggle's words, not the stored labels. */
export const DENOMINATION_LABELS: Record<BiotopausgleichKey, string> = {
  naturschutz_points: "Ökopunkte",
  naturschutz_area: "Ausgleichsfläche (ha)",
};
export function isBiotopausgleichKey(key: string): key is BiotopausgleichKey {
  return (BIOTOPAUSGLEICH_KEYS as readonly string[]).includes(key);
}
/** The denominations the statement holds, in the parent's own order (Ökopunkte first). */
export function biotopausgleichKeys(s?: RequestStatement | null): BiotopausgleichKey[] {
  return s ? BIOTOPAUSGLEICH_KEYS.filter((key) => s[key].selected) : [];
}
/**
 * Whether saving this statement would erase an amount the entity already has. Switching
 * denomination clears the amount on purpose — 50.000 Punkte are not 50.000 Hektar — so between
 * the switch and the new figure the statement names a supply with no quantity.
 */
export function dropsStatedQuantity(
  next: RequestStatement | null,
  currentQuantity: number | null
): boolean {
  const [held] = biotopausgleichKeys(next);
  return held !== undefined && currentQuantity != null && next?.[held].quantity == null;
}

/** Whether a Biotopausgleich need was stated at all — in either denomination. */
export function hasBiotopausgleich(s?: RequestStatement | null): boolean {
  return biotopausgleichKeys(s).length > 0;
}

/**
 * Whether the entity states a Biotopausgleich — the question every Ökopunkte-grain fact hangs
 * on: the Form (which denomination it trades in), its Menge, its price, and the
 * Kompensationsverordnung an Ökokonto is calculated under. The statement decides wherever there
 * is one; an offering that predates the statement has only its column to go on
 * (`offerings.naturschutz_supply`), and that column defaults to true — so a legacy Ökopunkte
 * offering keeps its grain instead of being read as a carrier.
 */
export function statesBiotopausgleich(
  statement: RequestStatement | null,
  naturschutzColumn: boolean | null | undefined
): boolean {
  return statement ? hasBiotopausgleich(statement) : naturschutzColumn !== false;
}

/**
 * Units the funnel may store per entry; the first is the default. Wertpunkte stay a Sales unit.
 * The subject rows (Artenschutz, Knick / Wallhecke / Allee) carry no quantity and no unit: an
 * obligation there is per species or per element, which the customer describes in `details` and
 * Sales turns into lines with a subject. The funnel never forces that model on the customer.
 */
export const STATEMENT_UNITS: Record<StatementKey, readonly string[]> = {
  naturschutz_points: ["oekopunkte"],
  naturschutz_area: ["ha"],
  forst: ["ha"],
  artenschutz: [],
  biotop: [],
  unknown: [],
};
/** The subject rows (a species or an element is the unit of obligation): free text INSTEAD of a quantity. */
export const SUBJECT_KEYS = ["artenschutz", "biotop"] as const;
export type SubjectKey = (typeof SUBJECT_KEYS)[number];
/** The rows that ACCEPT a description: the subject rows, and Forstausgleich beside its hectares. */
export const DESCRIBED_KEYS = [...SUBJECT_KEYS, "forst"] as const;
export type DescribedKey = (typeof DESCRIBED_KEYS)[number];
export function takesDetails(key: string): key is DescribedKey {
  return (DESCRIBED_KEYS as readonly string[]).includes(key);
}
export const DETAILS_MAX_LENGTH = 500;
export function isSubjectKey(key: string): key is SubjectKey {
  return (SUBJECT_KEYS as readonly string[]).includes(key);
}
/** Whole code points only, within the schema's limit in UTF-16 units: a symbol never gets split. */
function cutDetails(text: string): string {
  let out = "";
  for (const point of text) {
    if (out.length + point.length > DETAILS_MAX_LENGTH) break;
    out += point;
  }
  return out;
}
/** A row is named by a figure or by a non-blank text; a blank text names nothing. */
function namesEntry(e?: Partial<StatementEntry> | null): boolean {
  return e?.quantity != null || (typeof e?.details === "string" && e.details.trim() !== "");
}

/**
 * `label` is asked of the customer, `adminLabel` is read by Sales; neither contains a comma. The
 * two naturschutz labels name their denomination: the need itself is asked once, under
 * `BIOTOPAUSGLEICH_LABEL`. `detailsPlaceholder` hints what the description of that row could say —
 * one per row, since "Arten" makes sense for Artenschutz only. `helper` is the sentence under the
 * checkbox that says what happens next.
 */
export const STATEMENT_OPTIONS: Record<
  StatementKey,
  {
    label: string;
    adminLabel: string;
    /**
     * What the amount field is called, where the need's own name is not the thing being
     * measured: a Forstausgleich is owed as an Ersatzaufforstung, and its hectares are the
     * Ersatzaufforstungsfläche. The SUPPLY side names the same land from its own side, as an
     * Erstaufforstungsfläche — the buyer owes a duty, the seller has unforested land. Do not
     * harmonise the two. Defaults to `label` (see `statementQuantityLabel`).
     */
    quantityLabel?: string;
    axisKey: "naturschutz" | "forst" | "artenschutz" | "biotop" | null;
    detailsPlaceholder?: string;
    helper?: string;
  }
> = {
  naturschutz_points: { label: "Ökopunkte", adminLabel: "Ökopunkte", axisKey: "naturschutz" },
  naturschutz_area: {
    label: "Ausgleichsfläche",
    adminLabel: "Ausgleichsfläche",
    axisKey: "naturschutz",
  },
  forst: {
    label: "Forstausgleich (Waldumwandlung)",
    adminLabel: "Ersatzaufforstung",
    quantityLabel: "Ersatzaufforstungsfläche",
    axisKey: "forst",
    detailsPlaceholder: "z. B. besondere Anforderungen an Fläche, Baumarten oder Lage",
  },
  artenschutz: {
    label: "Artenschutzausgleich (CEF-Maßnahmen)",
    adminLabel: "Artenschutz",
    axisKey: "artenschutz",
    detailsPlaceholder: "z. B. Feldlerche, Kiebitz",
  },
  biotop: {
    label: "Sonderbiotopausgleich (Knick, Wallhecke, Allee)",
    adminLabel: "geschütztes Biotop (§30/§29)",
    axisKey: "biotop",
    detailsPlaceholder: "z. B. Knick, Wallhecke",
  },
  unknown: {
    label: "Ich bin mir nicht sicher – bitte rufen Sie mich an",
    adminLabel: "weiß nicht",
    axisKey: null,
    helper: "Wir melden uns telefonisch bei Ihnen — kostenfrei und unverbindlich.",
  },
};

/** The words on the amount field of a need: its own name unless the need names something else. */
export function statementQuantityLabel(key: StatementKey): string {
  const option = STATEMENT_OPTIONS[key];
  return option.quantityLabel ?? option.label;
}

const EntrySchema = z
  .object({
    selected: z.boolean(),
    quantity: z
      .number()
      .positive()
      .refine(TWO_DECIMALS_REFINE, TWO_DECIMALS_MESSAGE)
      .nullable()
      .optional(),
    unit: z.string().nullable().optional(),
    // Trimmed at the boundary; a blank text is no text (stored as absent, never as "").
    details: z
      .string()
      .trim()
      .max(DETAILS_MAX_LENGTH)
      .transform((text) => (text === "" ? undefined : text))
      .nullable()
      .optional(),
  })
  .strict();
export type StatementEntry = z.infer<typeof EntrySchema>;

const StatementObjectSchema = z
  .object({
    naturschutz_points: EntrySchema,
    naturschutz_area: EntrySchema,
    forst: EntrySchema,
    artenschutz: EntrySchema,
    biotop: EntrySchema,
    unknown: EntrySchema,
  })
  .strict()
  .superRefine((s, ctx) => {
    for (const key of STATEMENT_KEYS) {
      const e = s[key];
      if (e.quantity != null && !e.selected)
        ctx.addIssue({ code: "custom", path: [key], message: "Eine Menge braucht eine Auswahl." });
      if (e.unit != null && !STATEMENT_UNITS[key].includes(e.unit))
        ctx.addIssue({ code: "custom", path: [key, "unit"], message: "Unbekannte Einheit." });
      if (e.details != null && !e.selected)
        ctx.addIssue({ code: "custom", path: [key], message: "Details brauchen eine Auswahl." });
      if (isSubjectKey(key)) {
        if (e.quantity !== undefined || e.unit !== undefined)
          ctx.addIssue({
            code: "custom",
            path: [key],
            message: "Artenschutz und Biotop tragen keine Menge; Details als Text.",
          });
      } else if (e.details !== undefined && !takesDetails(key)) {
        ctx.addIssue({
          code: "custom",
          path: [key],
          message: "Nur Artenschutz, Biotop und Forstausgleich tragen Details.",
        });
      }
    }
    if (s.unknown.quantity !== undefined || s.unknown.unit !== undefined)
      ctx.addIssue({
        code: "custom",
        path: ["unknown"],
        message: "Weiß ich nicht trägt keine Menge.",
      });
    if (s.unknown.selected && NAMED_KEYS.some((k) => s[k].selected))
      ctx.addIssue({
        code: "custom",
        path: ["unknown"],
        message: "Weiß ich nicht kann nicht mit weiteren Pflichten kombiniert werden.",
      });
  });
export type RequestStatement = z.infer<typeof StatementObjectSchema>;
export const RequestStatementSchema = StatementObjectSchema.nullish();

export const EMPTY_STATEMENT: RequestStatement = {
  naturschutz_points: { selected: false },
  naturschutz_area: { selected: false },
  forst: { selected: false },
  artenschutz: { selected: false },
  biotop: { selected: false },
  unknown: { selected: false },
};

/**
 * Chat input: any subset of entries, each partial; a quantity or a text alone means selected.
 * The rows are typed by what they take, so a number on a subject row or a text on a quantity
 * row is refused (the model retries) instead of being dropped on the way to the statement.
 */
const QuantityTurnSchema = EntrySchema.omit({ details: true }).partial();
const DetailTurnSchema = EntrySchema.omit({ quantity: true, unit: true }).partial();
const DescribedQuantityTurnSchema = EntrySchema.partial();
export const RequestStatementToolSchema = z
  .object({
    naturschutz_points: QuantityTurnSchema.optional(),
    naturschutz_area: QuantityTurnSchema.optional(),
    forst: DescribedQuantityTurnSchema.optional(),
    artenschutz: DetailTurnSchema.optional(),
    biotop: DetailTurnSchema.optional(),
    unknown: z.object({ selected: z.boolean().optional() }).strict().optional(),
  })
  .strict()
  .optional()
  .describe(
    "Bedarf des Kunden, nur was er ausdrücklich nennt; eine Verneinung weglassen. Biotopausgleich in genau EINER Einheit: naturschutz_points (Ökopunkte) ODER naturschutz_area (Ausgleichsfläche in ha, auch flächiger Biotop-Ersatz) — nennt der Kunde beides, frage, welche gilt. forst = Forstausgleich (Waldumwandlung, in ha). artenschutz (saP/CEF) und biotop (Sonderbiotop, z. B. Knick, Wallhecke, Allee) haben keine Menge; details (auch bei forst) nimmt seine Worte wörtlich auf und darf fehlen. Menge nur übernehmen, wenn der Kunde sie genannt hat, und niemals umrechnen; erfrage sie nur, wenn das Tool sie als fehlende Angabe meldet. unknown: {selected: true} bei ausdrücklicher Unsicherheit — das ist zugleich die Bitte um einen Rückruf."
  );
export type RequestStatementInput = NonNullable<z.infer<typeof RequestStatementToolSchema>>;

function cleanEntry(key: StatementKey, e?: Partial<StatementEntry> | null): StatementEntry {
  const selected = e?.selected === true || (namesEntry(e) && e?.selected !== false);
  if (!selected || key === "unknown") return { selected };
  if (isSubjectKey(key)) {
    // A number named for a subject row selects it but is not kept: the row has no denomination.
    // The text stays as typed (the editor echoes it back), cut to the limit by whole code points;
    // the schema trims it at the boundary.
    const details = typeof e?.details === "string" ? cutDetails(e.details) : "";
    return details.trim() ? { selected, details } : { selected };
  }
  const unit = e?.unit && STATEMENT_UNITS[key].includes(e.unit) ? e.unit : STATEMENT_UNITS[key][0];
  const entry: StatementEntry =
    e?.quantity != null && e.quantity > 0
      ? { selected, quantity: e.quantity, unit }
      : { selected, unit };
  const details = takesDetails(key) && typeof e?.details === "string" ? cutDetails(e.details) : "";
  return details.trim() ? { ...entry, details } : entry;
}

/**
 * Normalise a partial statement into the full six-entry shape. „Weiß ich nicht" wins over every
 * named need; an entry that is not selected carries nothing; nothing selected is no answer (null).
 */
export function normaliseStatement(
  input?: Partial<Record<StatementKey, Partial<StatementEntry> | null>> | null
): RequestStatement | null {
  if (!input) return null;
  if (input.unknown?.selected === true) return { ...EMPTY_STATEMENT, unknown: { selected: true } };
  const out = { ...EMPTY_STATEMENT } as RequestStatement;
  for (const key of NAMED_KEYS) out[key] = cleanEntry(key, input[key]);
  return NAMED_KEYS.some((k) => out[k].selected) ? out : null;
}

/** A value read back from the database: anything unreadable is no answer. */
export function parseStoredStatement(value: unknown): RequestStatement | null {
  const parsed = RequestStatementSchema.safeParse(value ?? null);
  return parsed.success ? (parsed.data ?? null) : null;
}

/** The traded product's statement entry; other products have no naturschutz entry. */
export function statementKeyForProductType(
  type: "okopunkte" | "ausgleichsflaechen"
): "naturschutz_points" | "naturschutz_area";
export function statementKeyForProductType(
  type: string | undefined
): "naturschutz_points" | "naturschutz_area" | null;
export function statementKeyForProductType(
  type: string | undefined
): "naturschutz_points" | "naturschutz_area" | null {
  if (type === "okopunkte") return "naturschutz_points";
  if (type === "ausgleichsflaechen") return "naturschutz_area";
  return null;
}

export const CHAT_STATEMENT_REFUSAL_MESSAGES = {
  CHAT_STATEMENT_REQUIRED: "Bitte geben Sie den Bedarf oder die Art der Anfrage an.",
  CHAT_DENOMINATION_CONFLICT:
    "Biotopausgleich wird entweder in Ökopunkten oder als Ausgleichsfläche in Hektar angefragt. Frage den Nutzer, welche Einheit gilt, und rufe das Tool erneut mit nur einer der beiden Angaben auf.",
  CHAT_SCALAR_CONTRADICTION:
    "Die Menge widerspricht dem angegebenen Bedarf. Bitte rufe das Tool erneut mit korrigierten Angaben auf.",
  CHAT_SCALAR_DENOMINATION_MISMATCH:
    "Die Menge passt nicht zur Einheit des angegebenen Bedarfs. Frage den Nutzer, zu welchem Bedarf sie gehört, und rufe das Tool erneut auf.",
  CHAT_PRICE_DENOMINATION_MISMATCH:
    "Der maximale Preis passt nicht zur Einheit der Anfrage, die sich aus dem Bedarf ergibt. Bitte rufe das Tool erneut mit einem Preis in der passenden Einheit auf.",
} as const;
export type ChatStatementRefusalCode = keyof typeof CHAT_STATEMENT_REFUSAL_MESSAGES;

/** A cap can only be persisted in the denomination supplied by the chat caller. */
export function getRequestPriceDenominationError(
  price: number | undefined,
  priceType: string | undefined,
  parentType: string
): string | undefined {
  if (price !== undefined && priceType !== parentType)
    return CHAT_STATEMENT_REFUSAL_MESSAGES.CHAT_PRICE_DENOMINATION_MISMATCH;
  return undefined;
}

/**
 * Pure chat input normalisation, before any stored row is known. A type-less scalar accompanies
 * its matching answer (or sole named naturschutz need), not necessarily the points-first parent.
 * `newDraftRefusal` is checked only after lookup: reuse reconciles against stored answers instead.
 */
export function normaliseChatStatementTurn(input: {
  turn?: RequestStatementInput;
  type?: "okopunkte" | "ausgleichsflaechen";
  scalar?: number;
  price?: number;
}) {
  const { turn, type, scalar, price } = input;
  const incomingStatement = normaliseStatement(turn);
  const statedKeys = (["naturschutz_points", "naturschutz_area"] as const).filter(
    (key) => incomingStatement?.[key].selected
  );
  const scalarKey =
    (scalar !== undefined
      ? statedKeys.find((key) => incomingStatement?.[key].quantity === scalar)
      : undefined) ?? (statedKeys.length === 1 ? statedKeys[0] : undefined);
  let refusal: ChatStatementRefusalCode | undefined;
  if (!type && !incomingStatement) refusal = "CHAT_STATEMENT_REQUIRED";
  // One need, one denomination: a turn that states Ökopunkte AND Hektar states the customer's
  // Biotopausgleich twice, which the funnel cannot do either. `type` is Klara's product routing,
  // not the customer's words, so it still folds in below.
  else if (statedKeys.length === BIOTOPAUSGLEICH_KEYS.length)
    refusal = "CHAT_DENOMINATION_CONFLICT";
  else if (scalar !== undefined && !type && statedKeys.length > 0 && !scalarKey)
    refusal = "CHAT_SCALAR_CONTRADICTION";
  const scalarType =
    type ??
    (scalarKey === "naturschutz_area"
      ? "ausgleichsflaechen"
      : statedBiotopausgleich(incomingStatement).type);
  const productKey = statementKeyForProductType(scalarType);
  const statement = fillBiotopausgleichQuantity(
    type
      ? mergeStatement(incomingStatement, { [productKey]: { selected: true } })
      : incomingStatement,
    scalarType,
    scalar
  );
  const biotopausgleich = statedBiotopausgleich(statement);
  const mixedNaturschutzDemand = !!(
    statement?.naturschutz_points.selected && statement.naturschutz_area.selected
  );
  const statedQuantity = incomingStatement?.[productKey].quantity;
  let newDraftRefusal = refusal;
  if (!newDraftRefusal && getRequestPriceDenominationError(price, scalarType, biotopausgleich.type))
    newDraftRefusal = "CHAT_PRICE_DENOMINATION_MISMATCH";
  if (
    !newDraftRefusal &&
    scalar !== undefined &&
    ((!type && !incomingStatement?.[productKey].selected) ||
      (statedQuantity != null && scalar !== statedQuantity))
  )
    newDraftRefusal = "CHAT_SCALAR_CONTRADICTION";
  return {
    incomingStatement,
    statement,
    biotopausgleich,
    scalarType,
    productKey,
    statedKeys,
    mixedNaturschutzDemand,
    refusal,
    newDraftRefusal,
  };
}

/**
 * A statement naming only this Biotopausgleich: what a request without a statement states before
 * the first partial chat statement merges into it. None when it names no Biotopausgleich.
 */
export function statementOfBiotopausgleich(
  type: string | undefined,
  quantity: number | null | undefined,
  stated = true
): RequestStatement | null {
  if (!stated) return null;
  const key = statementKeyForProductType(type);
  return key ? normaliseStatement({ [key]: { selected: true, quantity } }) : null;
}

/** Fill the selected entry of `type`'s denomination with `quantity`, never overwriting an answer. */
export function fillBiotopausgleichQuantity(
  statement: RequestStatement | null,
  type: string | undefined,
  quantity: number | null | undefined
): RequestStatement | null {
  const scalarStatement = statementOfBiotopausgleich(type, quantity);
  let enriched = statement;
  for (const key of STATEMENT_KEYS) {
    const entry = enriched?.[key];
    const quantity = scalarStatement?.[key].quantity;
    if (entry?.selected && entry.quantity == null && quantity != null)
      enriched = mergeStatement(enriched, { [key]: { quantity } });
  }
  return enriched;
}

/**
 * Fold what the customer said this turn into what they said before. Omitted entries keep their
 * stored value; `selected: false` is an explicit retraction; `unknown` replaces everything.
 */
export function mergeStatement(
  stored: RequestStatement | null,
  incoming?: RequestStatementInput | null
): RequestStatement | null {
  if (!incoming) return stored;
  if (incoming.unknown?.selected === true)
    return normaliseStatement({ unknown: { selected: true } });
  const merged: Partial<Record<StatementKey, Partial<StatementEntry>>> = { ...(stored ?? {}) };
  for (const key of NAMED_KEYS) {
    const turn = incoming[key] as Partial<StatementEntry> | undefined;
    if (turn === undefined) continue;
    // A quantity or a text named this turn selects the row unless the turn retracts it explicitly.
    const selected = turn.selected ?? (namesEntry(turn) ? true : stored?.[key]?.selected);
    merged[key] = { ...(stored?.[key] ?? {}), ...turn, selected };
  }
  if (NAMED_KEYS.some((k) => merged[k]?.selected || namesEntry(merged[k])))
    merged.unknown = { selected: false };
  return normaliseStatement(merged);
}

/** Select or deselect one row; a named row deselects „weiß nicht", and „weiß nicht" clears the rest. */
export function toggleStatementEntry(
  s: RequestStatement | null,
  key: StatementKey
): RequestStatement | null {
  const current = s ?? EMPTY_STATEMENT;
  const selected = !current[key].selected;
  if (key === "unknown")
    return selected ? normaliseStatement({ unknown: { selected: true } }) : null;
  return normaliseStatement({
    ...current,
    unknown: { selected: false },
    [key]: { ...current[key], selected },
  });
}

/** Set a quantity row's amount (null clears it) in its unit. */
export function setStatementQuantity(
  s: RequestStatement | null,
  key: NamedStatementKey,
  quantity: number | null,
  unit?: string
): RequestStatement | null {
  const current = s ?? EMPTY_STATEMENT;
  return normaliseStatement({
    ...current,
    unknown: { selected: false },
    [key]: { ...current[key], selected: true, quantity, unit: unit ?? current[key].unit },
  });
}

/** Set a row's description as typed (blank clears it); any text selects the row. */
export function setStatementDetails(
  s: RequestStatement | null,
  key: DescribedKey,
  details: string
): RequestStatement | null {
  const current = s ?? EMPTY_STATEMENT;
  return normaliseStatement({
    ...current,
    unknown: { selected: false },
    [key]: { ...current[key], selected: true, details },
  });
}

/** Round editor quantities exactly as the locale display does; zero clears only the amount. */
export function roundStatementQuantities(
  s: RequestStatement | null,
  decimalsFor: (key: NamedStatementKey, unit: string) => number
): RequestStatement | null {
  if (!s) return s;
  let next = s;
  for (const key of NAMED_KEYS) {
    const entry = s[key];
    if (!entry.selected || entry.quantity == null) continue;
    const unit = entry.unit ?? STATEMENT_UNITS[key][0];
    // Intl and toFixed disagree on decimal ties such as 1.005. Use the display's rounding.
    const quantity = Number(
      entry.quantity.toLocaleString("en-US", {
        useGrouping: false,
        maximumFractionDigits: decimalsFor(key, unit),
      })
    );
    if (quantity !== entry.quantity) {
      next = {
        ...next,
        [key]: quantity > 0 ? { ...entry, quantity } : { ...entry, quantity: undefined },
      };
    }
  }
  return next;
}

/** A retired unit never retains a hidden amount: a quantity row whose unit is gone is deselected. */
export function reconcileStatementUnits(
  s: RequestStatement | null,
  availableUnitsFor: (key: NamedStatementKey) => readonly string[]
): RequestStatement | null {
  let next = s;
  for (const key of NAMED_KEYS) {
    if (!next?.[key].selected || isSubjectKey(key)) continue;
    const unit = next[key].unit ?? STATEMENT_UNITS[key][0];
    if (!availableUnitsFor(key).includes(unit)) next = toggleStatementEntry(next, key);
  }
  return next;
}

/** The selected entries, for analytics and tests — keys only, never amounts. */
export function statementSelectedKeys(s?: RequestStatement | null): StatementKey[] {
  return s ? STATEMENT_KEYS.filter((k) => s[k].selected) : [];
}

/** Sales' one-line rendering: „Ökopunkte 4.200.000 · Ersatzaufforstung 12,00 ha · Artenschutz". */
export function formatStatement(s?: RequestStatement | null): string {
  if (!s) return "keine Angabe";
  if (s.unknown.selected) return STATEMENT_OPTIONS.unknown.adminLabel;
  const parts = NAMED_KEYS.filter((k) => s[k].selected).map((k) => {
    const e = s[k];
    const label = STATEMENT_OPTIONS[k].adminLabel;
    if (e.quantity == null) return label;
    return `${label} ${formatQuantityWithUnitKey(e.quantity, e.unit ?? STATEMENT_UNITS[k][0])}`;
  });
  return parts.length ? parts.join(" · ") : "keine Angabe";
}

/**
 * „Weiß ich nicht" is the funnel's callback request — the customer said they cannot name their
 * need and asked to be phoned. Sales reads that before anything else, so it is named as such.
 */
export const CALLBACK_REQUESTED_NOTE = "Rückruf gewünscht (Schnellanfrage)";

/**
 * A stored statement as the one line Sales reads on a lead — on the board, on the Teams card.
 * Null when nothing was stated: the parent columns carry only the Biotopausgleich denomination the
 * request is filed under, so a lead for Ersatzaufforstung or Artenschutz would otherwise read blank.
 */
export function describeStoredStatement(value: unknown): string | null {
  const statement = parseStoredStatement(value);
  if (!statement) return null;
  return statement.unknown.selected ? CALLBACK_REQUESTED_NOTE : formatStatement(statement);
}

/**
 * The Biotopausgleich a statement names, as one product (D13): Ökopunkte when selected, otherwise
 * hectares, otherwise none (`stated` false, with Ökopunkte as the market's default vocabulary).
 */
export function statedBiotopausgleich(s: RequestStatement | null): {
  type: "okopunkte" | "ausgleichsflaechen";
  quantity: number | null;
  stated: boolean;
} {
  if (s?.naturschutz_points.selected)
    return { type: "okopunkte", quantity: s.naturschutz_points.quantity ?? null, stated: true };
  if (s?.naturschutz_area.selected)
    return {
      type: "ausgleichsflaechen",
      quantity: s.naturschutz_area.quantity ?? null,
      stated: true,
    };
  return { type: "okopunkte", quantity: null, stated: false };
}
